/*
 * Photo Headshot — places the selected photo in Azure Blob Storage so
 * photoHeadApi.ts's POST /image/photo-head has a blob reference to read
 * (that endpoint downloads the blob server-side; it does not accept inline
 * base64 — see photoHeadApi.ts's header comment).
 *
 * Upload mechanics (blob-sas request + PUT) live in shared/blobImageUpload.ts,
 * shared with sketchToSlideUpload.ts. Only this feature's allowed-format
 * table and file naming stay here.
 */

import type { HttpClient, HttpRequestOptions } from "../../../services/deckorateApi";
import type { BlobUploadReasons, BlobUploadResult, ImageFormatSignature } from "../../shared/services/blobImageUpload";
import { detectExtensionFromBytes, uploadImageBlob } from "../../shared/services/blobImageUpload";
import { logPhotoHeadError } from "./photoHeadApi";

export type PhotoHeadUploadResult = BlobUploadResult;

const BLOB_SAS_REQUEST_FAILED_MESSAGE =
  "Couldn't get a place to upload this photo — the blob-sas request didn't return a usable asset.";
const BLOB_PUT_FAILED_MESSAGE = "Uploading the photo to blob storage failed.";

const UPLOAD_MESSAGES = { blobSasFailed: BLOB_SAS_REQUEST_FAILED_MESSAGE, blobPutFailed: BLOB_PUT_FAILED_MESSAGE };

const UPLOAD_REASONS: BlobUploadReasons = {
  formatSniff: "photo-head-format-sniff",
  sasRequest: "photo-head-blob-sas-request",
  sasVersionParse: "photo-head-sas-version-parse",
  putEncode: "photo-head-blob-put-encode",
  putNetwork: "photo-head-blob-put-network",
  putBodyRead: "photo-head-blob-put-body-read",
};

const EXTENSION_BY_MIME_TYPE: Record<string, string> = {
  "image/png": "png",
  "image/jpeg": "jpg",
  "image/jpg": "jpg",
  "image/gif": "gif",
  "image/bmp": "bmp",
  "image/webp": "webp",
};

/** Magic-number signatures, checked in order — first bytes of the decoded image. */
const FORMAT_SIGNATURES: readonly ImageFormatSignature[] = [
  { extension: "png", checks: [{ offset: 0, bytes: [0x89, 0x50, 0x4e, 0x47] }] },
  { extension: "jpg", checks: [{ offset: 0, bytes: [0xff, 0xd8, 0xff] }] },
  { extension: "gif", checks: [{ offset: 0, bytes: [0x47, 0x49, 0x46, 0x38] }] },
  { extension: "bmp", checks: [{ offset: 0, bytes: [0x42, 0x4d] }] },
];

/** Longest signature above needs 4 bytes; base64 chars come in groups of 4 -> 3 bytes,
 * so 8 chars (2 groups) safely decodes to 6 bytes without a padding edge case. */
const SIGNATURE_PROBE_BASE64_LENGTH = 8;

function fileNameFor(base64: string, mimeType: string): string {
  const extension =
    detectExtensionFromBytes(base64, FORMAT_SIGNATURES, SIGNATURE_PROBE_BASE64_LENGTH, logPhotoHeadError, UPLOAD_REASONS.formatSniff) ??
    EXTENSION_BY_MIME_TYPE[mimeType] ??
    "png";
  return `photo.${extension}`;
}

/**
 * Upload the given photo to Azure Blob Storage and return a blob reference
 * for photoHeadApi.ts's POST /image/photo-head.
 *
 * @param http Deckorate HttpClient (used only for the blob-sas request).
 * @param clientId Studio client id (StudioClient.id) — the tenant the blob belongs to.
 * @param base64 Image bytes, base64-encoded, no `data:` prefix. The file_name sent to
 *   blob-sas is derived from these bytes' actual magic-number signature, not `mimeType`,
 *   so it reflects the real format even if the caller's mimeType label is wrong.
 * @param mimeType e.g. "image/png" — used as a fallback only if signature sniffing fails,
 *   and as the blob's Content-Type on upload.
 */
export async function uploadPhotoHeadImage(
  http: HttpClient,
  clientId: number,
  base64: string,
  mimeType: string,
  options: HttpRequestOptions = {},
): Promise<PhotoHeadUploadResult> {
  return uploadImageBlob({
    http,
    clientId,
    base64,
    mimeType,
    fileName: fileNameFor(base64, mimeType),
    options,
    messages: UPLOAD_MESSAGES,
    onError: logPhotoHeadError,
    reasons: UPLOAD_REASONS,
  });
}
